import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { XAlertService } from '../x-alert.service';
import { WarehouseApiService } from './warehouse-api.service';
import { RequestModel } from '@core/models/request.model';
import {
  CheckDeviceSoldAttemptResult,
  SellDeviceSuccessBackendResponse,
  WarehouseSellDeviceResult,
  WarehouseServiceTranslations,
  WarehouseServiceTranslationsPayload,
} from '@core/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private readonly warehouseApiService = inject(WarehouseApiService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private translates: WarehouseServiceTranslationsPayload = {
    [WarehouseServiceTranslations.SellRecoverErrorNoRights]:
      'Не удалось списать/восстановить устройство со склада: у вас нет прав.',
    [WarehouseServiceTranslations.SellRecoverErrorNotFound]:
      'Не удалось списать/восстановить устройство со склада: не найдено на складе.',
    [WarehouseServiceTranslations.SellRecoverErrorUndefinedError]:
      'Не удалось списать/восстановить устройство со склада: неизвестная ошибка.',
    [WarehouseServiceTranslations.CheckSoldUndefinedError]:
      'Неизвестная ошибка списания устройства',
    [WarehouseServiceTranslations.CheckSoldForbidden]:
      'Вам нельзя списывать данное устройство',
    [WarehouseServiceTranslations.CheckSoldNotFound]:
      'Устройство не найдено на складе',
    [WarehouseServiceTranslations.CheckSoldMayBeProblems]:
      'Возможно, будут проблемы со списанием устройства при закрытии заявки. Причина:',
  };

  constructor() {
    this.outkeepTranslateService
      .translate(Object.values(WarehouseServiceTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  sellDevice$(
    installerId: number,
    sim1: string,
  ): Observable<WarehouseSellDeviceResult> {
    return this.warehouseApiService.sellDevice$(installerId, sim1).pipe(
      map(result => this.handleSoldStatusDeviceSuccessResult(sim1, result)),
      catchError(error =>
        of(this.handleSoldStatusDeviceRequestError(sim1, error)),
      ),
    );
  }

  unSellDevice$(request: RequestModel, sim1: string): Observable<null> {
    const installerId = request.idOfInstaller;

    if (!installerId) {
      return of(null);
    }

    return this.warehouseApiService.unSellDevice$(installerId, sim1).pipe(
      map(result => this.handleSoldStatusDeviceSuccessResult(sim1, result)),
      catchError(error =>
        of(this.handleSoldStatusDeviceRequestError(sim1, error)),
      ),
      switchMap(result => {
        console.debug('unSellDevice$ result: ', result);
        // if (!result.isSuccess) {
        //   this.xAlertService.showMessage(result.errorText, {
        //     autoClose: false,
        //     status: 'error',
        //   });
        // }

        return of(null);
      }),
    );
  }

  private handleSoldStatusDeviceSuccessResult(
    sim1: string,
    successResult: SellDeviceSuccessBackendResponse,
  ): WarehouseSellDeviceResult {
    if (successResult?.sim_1ListNotExist?.length)
      return this.getFailureSellDeviceResult(sim1, HttpStatusCode.NotFound);

    return { isSuccess: true };
  }

  private handleSoldStatusDeviceRequestError(
    sim1: string,
    error: HttpErrorResponse,
  ): WarehouseSellDeviceResult {
    if (error.status === 0 || error.status === HttpStatusCode.Unauthorized) {
      throw new Error(error.message);
    }

    return this.getFailureSellDeviceResult(sim1, error.status);
  }

  private getFailureSellDeviceResult(
    sim1: string,
    errorStatus: HttpStatusCode,
  ) {
    if (errorStatus === HttpStatusCode.Forbidden) {
      return {
        isSuccess: false,
        errorText: `"${sim1}". ${this.translates[WarehouseServiceTranslations.SellRecoverErrorNoRights]}`,
      };
    } else if (errorStatus === HttpStatusCode.NotFound) {
      return {
        isSuccess: false,
        errorText: `"${sim1}". ${this.translates[WarehouseServiceTranslations.SellRecoverErrorNotFound]}`,
      };
    } else {
      return {
        isSuccess: false,
        errorText: `"${sim1}". ${this.translates[WarehouseServiceTranslations.SellRecoverErrorUndefinedError]}`,
      };
    }
  }

  /**
   * Проверить возможность списания устройства со склада
   */
  checkDeviceSoldAttempt$(
    installerId: number,
    sim1: string,
    options = {
      showUserAlertIfError: true,
    },
  ): Observable<CheckDeviceSoldAttemptResult> {
    return this.warehouseApiService
      .checkDeviceSoldAttempt$(installerId, sim1)
      .pipe(
        map(() => ({ isSuccess: true }) as const),
        catchError((err: HttpErrorResponse) => {
          switch (err.status) {
            case HttpStatusCode.BadRequest:
            case HttpStatusCode.InternalServerError:
              return of({
                isSuccess: false,
                errorText:
                  this.translates[
                    WarehouseServiceTranslations.CheckSoldUndefinedError
                  ],
              });
            case HttpStatusCode.Forbidden:
              return of({
                isSuccess: false,
                errorText:
                  err?.error?.message ??
                  this.translates[
                    WarehouseServiceTranslations.CheckSoldForbidden
                  ],
              });
            case HttpStatusCode.NotFound:
              return of({
                isSuccess: false,
                errorText:
                  this.translates[
                    WarehouseServiceTranslations.CheckSoldNotFound
                  ],
              });
            default:
              return throwError(() => err);
          }
        }),
        tap(result => {
          if (!result.isSuccess && options.showUserAlertIfError) {
            this.xAlertService.showMessage(
              `${
                this.translates[
                  WarehouseServiceTranslations.CheckSoldMayBeProblems
                ]
              } ${result.errorText}`,
              { appearance: 'warning', autoClose: 0 },
            );
          }
        }),
      );
  }
}
